import type { Rule, TemplateName } from '../../core/assetType.js';
import type { ValidationIssue } from '../../core/issues.js';
import type { ContentAssetSets, ContentAssetTypeSets } from '../../core/registries.js';

import { isFlipCardSlugKey } from '../../activities/flipCardFaces.js';
import { flipCards } from '../../activities/flipCards.js';
import { image, IMAGE_ACTIVITY } from '../../activities/image.js';
import { SWIPE_CARD_IMAGE } from '../../activities/swipeCardImage.js';
import { swipeSelect } from '../../activities/swipeSelect.js';
import { toPointer } from '../../core/issues.js';

type SlugCategory = 'background' | 'character' | 'image';

/**
 * Identifies the slug fields that MUST use a content asset and their category
 * (background vs. character). Backgrounds are validated against the template's
 * `backgrounds` list; narrators and character avatars against its `characters`
 * list. Returns null for any field that carries no registry obligation.
 *
 *   - any `*ContentAssetSlug` key containing "background" → background
 *     (defaultBackgroundContentAssetSlug, backgroundContentAssetSlug,
 *     backgroundMobileContentAssetSlug, …)
 *   - narratorContentAssetSlug → character (the narrator is a character asset)
 *   - characters[].contentAssetSlug (the bare key, only inside a `characters`
 *     array — NOT activity option slugs of the same name) → character
 *   - a flip card face's slug (`frontContentAssetSlug`, `backContentAssetSlug`, …) on a card of a
 *     `flip_cards` activity, a swipe card's image slug (the bare key directly on a `cards[]`
 *     entry of a `swipe_select` activity) or an image node's slug (the bare key directly on an
 *     `image` activity's `setup`) → image, which only has to exist: any kind, placed under any
 *     template. Other activities share `setup.cards`, but their cards own no images.
 *
 * Activity option images (options[].contentAssetSlug) and scroll-card
 * backgrounds are intentionally NOT validated — they have no registry.
 */
const enforcedCategory = (
  { key, path, activityType }: { key: string; path: (string | number)[]; activityType: string | undefined },
): SlugCategory | null => {
  if (/background.*ContentAssetSlug$/i.test(key)) {
    return 'background';
  }
  if (key === 'narratorContentAssetSlug') {
    return 'character';
  }
  if (key === 'contentAssetSlug' && path.includes('characters')) {
    return 'character';
  }
  if (isFlipCardSlugKey(key) && activityType === flipCards.type && path.includes('cards')) {
    return 'image';
  }
  if (key === SWIPE_CARD_IMAGE.slugKey && activityType === swipeSelect.type && path.at(-2) === 'cards') {
    return 'image';
  }
  if (key === IMAGE_ACTIVITY.slugKey && activityType === image.type && path.at(-1) === 'setup') {
    return 'image';
  }
  return null;
};

interface FoundSlug {
  path: (string | number)[];
  value: unknown;
  category: SlugCategory;
  /** The cast member's id, when the slug is one's avatar. */
  characterId?: string;
}

/** An activity is the object holding a `setup`; its `type` scopes what the setup's fields mean. */
const activityTypeOf = (record: Record<string, unknown>): string | undefined =>
  'setup' in record && typeof record.type === 'string' ? record.type : undefined;

/**
 * Recursively collects the values of the enforced background/character/narrator
 * slug fields, paired with their path from the inner root, their category, and
 * the id of the cast member an avatar belongs to. `activityType` is the type of
 * the nearest enclosing activity, if any.
 */
const collectSlugFields = (
  { node, path, activityType, out }: { node: unknown; path: (string | number)[]; activityType?: string | undefined; out: FoundSlug[] },
): void => {
  if (Array.isArray(node)) {
    node.forEach((item, idx) => collectSlugFields({ node: item, path: [...path, idx], activityType, out }));
    return;
  }
  if (node && typeof node === 'object') {
    const record = node as Record<string, unknown>;
    const scope = activityTypeOf(record) ?? activityType;
    for (const [key, value] of Object.entries(record)) {
      const keyPath = [...path, key];
      const category = enforcedCategory({ key, path, activityType: scope });
      if (category) {
        const id = key === 'contentAssetSlug' ? (node as Record<string, unknown>).id : undefined;
        out.push({ path: keyPath, value, category, ...(typeof id === 'string' ? { characterId: id } : {}) });
      }
      collectSlugFields({ node: value, path: keyPath, activityType: scope, out });
    }
  }
};

/** The id of the node a slug's path starts under, when it has a string one. */
const nodeIdOf = (input: unknown, path: (string | number)[]): string | undefined => {
  const [root, index] = path;
  if (root !== 'nodes' || typeof index !== 'number') {
    return undefined;
  }
  const id = ((input as { nodes?: { id?: unknown }[] }).nodes?.[index])?.id;
  return typeof id === 'string' ? id : undefined;
};

/** Resolves the allowed background/character sets for a given template. */
const setsFor = (registry: ContentAssetSets, type: TemplateName): ContentAssetTypeSets =>
  // `dil` takes story's entries of a kind when none names it; buildRegistries
  // has already placed them, so registry.dil is complete here.
  registry[type];

/**
 * content-asset.slug-unknown — every background, narrator, and character avatar
 * slug used in the inner content must appear in the slot's list for THIS
 * template in the content-assets registry. The registry is keyed by template,
 * so an entry is refused in a template its own `templates` leaves out. A
 * character starts with every template chosen, so it is castable everywhere
 * until someone narrows its list; backgrounds keep the lists they were given.
 *
 * Empty/undefined slugs are ignored here (the story-specific `slug-missing`
 * rule handles the "needs one of slug/url" case). Other `*ContentAssetSlug`
 * fields (activity option images, scroll-card backgrounds) are not validated.
 *
 * Characters and narrators are always checked, even against an empty list: an
 * uncatalogued environment must not read green. Backgrounds are checked only
 * once the template has any, so the check switches on as the catalogue grows
 * (`dil`'s set is empty only when `story`'s is too). A refused avatar carries
 * its cast member's id as the locator; any other refused slug under a node
 * carries that node's id.
 */
export const contentAssetSlugsKnown = (type: TemplateName): Rule<unknown> => ({
  code: 'content-asset.slug-unknown',
  check: (input, ctx): ValidationIssue[] => {
    const registry = ctx.registries.contentAssets;

    const sets = setsFor(registry, type);
    const found: FoundSlug[] = [];
    collectSlugFields({ node: input, path: [], out: found });

    const issues: ValidationIssue[] = [];
    for (const { path, value, category, characterId } of found) {
      // Only validate non-empty string slugs; absent/empty is out of scope.
      if (typeof value !== 'string' || value.length === 0) {
        continue;
      }
      if (category === 'image') {
        if (!registry.all.has(value)) {
          issues.push({
            code: 'content-asset.slug-unknown',
            severity: 'error',
            message: `Unknown content asset slug '${value}': an image must be a content asset in the content-assets registry.`,
            path,
            pointer: toPointer(path),
            currentValue: value,
            constraint: { kind: 'reference' },
          });
        }
        continue;
      }
      if (category === 'background' && sets.backgrounds.size === 0) {
        continue;
      }
      const allowedSet = category === 'background' ? sets.backgrounds : sets.characters;
      if (allowedSet.has(value)) {
        continue;
      }
      const allowed = [...allowedSet].sort();
      const listName = category === 'background' ? 'backgrounds' : 'characters';
      const nodeId = characterId ? undefined : nodeIdOf(input, path);
      issues.push({
        code: 'content-asset.slug-unknown',
        severity: 'error',
        message: `Unknown content asset slug '${value}' for ${type}: ${category} slugs must come from the content-assets registry's "${type}".${listName}.`,
        path,
        pointer: toPointer(path),
        ...(characterId ? { locator: { characterId } } : nodeId ? { locator: { nodeId } } : {}),
        currentValue: value,
        constraint: { kind: 'reference', allowedValues: allowed },
      });
    }

    return issues;
  },
});
